import { z } from 'zod';

// Global, tenant-less question catalog — see root CLAUDE.md P4/P5 on why
// cross-tenant authoring is super-admin-only. `order` decides both the edit
// form's field order and the order the client-facing text is assembled in.
// `key` is the seed layer's natural key (`authoritativeSync` on `key`) —
// stable across re-seeds even when the wording changes.
export const TermsOfUseQuestionDto = z.object({
  key: z.string().trim().min(1),
  questionText: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
  order: z.number().int().nonnegative(),
});
export type ApiTermsOfUseQuestionDto = z.infer<typeof TermsOfUseQuestionDto>;

export const UpdateTermsOfUseQuestionDto = z.object({
  questionText: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).optional(),
  order: z.number().int().nonnegative().optional(),
  isActive: z.boolean().optional(),
});
export type ApiUpdateTermsOfUseQuestionDto = z.infer<
  typeof UpdateTermsOfUseQuestionDto
>;

export const TermsOfUseAnswerDto = z.object({
  questionId: z.string().uuid(),
  textValue: z.string().trim().min(1),
});
export type ApiTermsOfUseAnswerDto = z.infer<typeof TermsOfUseAnswerDto>;

// Saving always re-sends every active question's answer — the service snapshots
// the full set into a new version only when something actually changed.
// Contact email/phone are mandatory (the official ToU template marks them
// with *); the social links are optional.
export const SaveOrgTermsOfUseDto = z.object({
  contactEmail: z.string().trim().email(),
  contactPhone: z.string().trim().min(1),
  website: z.string().trim().min(1).nullish(),
  instagram: z.string().trim().min(1).nullish(),
  linkedin: z.string().trim().min(1).nullish(),
  tiktok: z.string().trim().min(1).nullish(),
  answers: z.array(TermsOfUseAnswerDto),
});
export type ApiSaveOrgTermsOfUseDto = z.infer<typeof SaveOrgTermsOfUseDto>;

// Requests verification for a contact email before any content is saved —
// see `OrgTermsOfUseService.requestContactEmailVerification`.
export const RequestOrgTermsOfUseContactEmailVerificationDto = z.object({
  contactEmail: z.string().trim().email(),
});
export type ApiRequestOrgTermsOfUseContactEmailVerificationDto = z.infer<
  typeof RequestOrgTermsOfUseContactEmailVerificationDto
>;

// Membership TOU's own contact section (marketplaceStyle dialog) —
// deliberately independent of `SaveOrgTermsOfUseDto`'s identically-named
// fields: an org may run memberships without ever listing on the
// marketplace, so the two must never be coupled. All optional — the plain
// (non-marketplaceStyle) form never sends them. `membershipId` (in the
// controller route, not this body) omitted → org-wide default; present → a
// trainer's override tied to exactly that membership.
export const SaveMembershipTermsOfUseDto = z
  .object({
    answers: z.array(TermsOfUseAnswerDto),
    contactEmail: z.string().trim().email().optional(),
    contactPhone: z.string().trim().min(1).optional(),
    website: z.string().trim().min(1).nullish(),
    instagram: z.string().trim().min(1).nullish(),
    linkedin: z.string().trim().min(1).nullish(),
    tiktok: z.string().trim().min(1).nullish(),
  })
  .refine((dto) => Boolean(dto.contactEmail) === Boolean(dto.contactPhone), {
    message: 'contactEmail and contactPhone must be provided together',
    path: ['contactPhone'],
  });
export type ApiSaveMembershipTermsOfUseDto = z.infer<
  typeof SaveMembershipTermsOfUseDto
>;

// The address to send the link to — always the live-typed one, which may
// differ from the membership's currently saved `contactEmail` (an
// in-progress edit nobody has saved yet). See
// `MembershipTermsOfUseService.resendContactEmailVerification`.
export const ResendMembershipTermsOfUseContactEmailVerificationDto = z.object({
  contactEmail: z.string().trim().email(),
});
export type ApiResendMembershipTermsOfUseContactEmailVerificationDto = z.infer<
  typeof ResendMembershipTermsOfUseContactEmailVerificationDto
>;

// The version id is the consent itself (same pattern as marketplace
// checkout's `acceptedTermsVersionId`) — accepting a STALE version (the
// effective one moved on since the client loaded it) is rejected server-side
// rather than silently accepted.
export const AcceptMembershipTermsOfUseDto = z.object({
  versionId: z.string().uuid(),
});
export type ApiAcceptMembershipTermsOfUseDto = z.infer<
  typeof AcceptMembershipTermsOfUseDto
>;
